import {skipToken} from '@reduxjs/toolkit/query';
import {
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import * as Yup from 'yup';

import {isNil, isNotNil} from 'ramda';

import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {useGenerateAiVehicleServiceIntervalsMutation} from '@dms/api/vehicle';
import {
  AdditionalInformationItem,
  useGetServiceVehicleAdditionalInformationQuery,
  useGetServiceVehicleQuery,
  usePatchServiceVehicleAdditionalInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';

// Additional-information item codes carrying the TecRMI vehicle identification. The user picks the
// type/variant here; the patch persists them onto the service vehicle and the backend reads them
// back when generating the service-interval plan. The API returns the code in PascalCase, so all
// matching is case-insensitive.
const TEC_RMI_VEHICLE_TYPE_CODE = 'tecRmiVehicleType';
const TEC_RMI_VARIANT_CODE = 'tecRmiVehicleCooGroup';

type GenerateServiceIntervalsFormValues = {
  model: string;
  mileage: number;
  type: string;
  variant: string;
};

interface VehicleServiceIntervalsGenerateFormProps extends RequiredTestIdProps {
  vehicleData: GetVehicleApiResponseV2;
  onSuccess: () => void;
  onCancel: () => void;
}

export function VehicleServiceIntervalsGenerateForm(
  props: VehicleServiceIntervalsGenerateFormProps
) {
  const serviceVehicleQuery = useGetServiceVehicleQuery({vehicleId: props.vehicleData.id});
  const serviceVehicleId = serviceVehicleQuery.data?.serviceVehicleId;

  const additionalInfoQuery = useGetServiceVehicleAdditionalInformationQuery(
    isNotNil(serviceVehicleId) ? {serviceVehicleId} : skipToken
  );

  const [generateAiVehicleServiceIntervals] = useGenerateAiVehicleServiceIntervalsMutation();
  const [patchAdditionalInformation, {isLoading: isPatching}] =
    usePatchServiceVehicleAdditionalInformationMutation();

  const additionalInformation = additionalInfoQuery.data ?? [];
  const vehicleTypeItem = findByCode(additionalInformation, TEC_RMI_VEHICLE_TYPE_CODE);
  const variantItem = findByCode(additionalInformation, TEC_RMI_VARIANT_CODE);

  const typeOptions = toOptions(vehicleTypeItem);
  const variantOptions = toOptions(variantItem);

  // The plan, suggestions and AI summary are computed on the backend; the frontend only collects
  // the mileage. The TecRMI type/variant are persisted via the additional-information patch above.
  const onSubmit: FormSubmitHandler<GenerateServiceIntervalsFormValues> = async (values) =>
    await generateAiVehicleServiceIntervals({
      vehicleId: props.vehicleData.id,
      body: {currentMileage: values.mileage},
    })
      .unwrap()
      .then(() => props.onSuccess())
      .catch(handleApiError);

  return (
    <DataStatus
      isLoading={serviceVehicleQuery.isLoading || additionalInfoQuery.isLoading}
      isError={serviceVehicleQuery.isError || additionalInfoQuery.isError}
      minHeight={50}
    >
      <Form<GenerateServiceIntervalsFormValues>
        onSubmit={onSubmit}
        schema={schema}
        defaultValues={{
          model: props.vehicleData.title,
          mileage: props.vehicleData.mileage ?? undefined,
          type: getSelectedValue(vehicleTypeItem) ?? typeOptions[0]?.value,
          variant: getSelectedValue(variantItem) ?? variantOptions[0]?.value,
        }}
      >
        {(control, formApi) => {
          // Changing the type re-derives the variant on the backend: patch the type, refetch the
          // additional information, then reset the variant to the freshly computed default.
          const handleTypeChange = (typeValue: string | number | Nullish) => {
            if (isNil(serviceVehicleId) || isNil(vehicleTypeItem?.id) || isNil(typeValue)) {
              return;
            }

            patchAdditionalInformation({
              serviceVehicleId,
              additionalInformationId: vehicleTypeItem.id,
              patchAdditionalInformationRequestBody: {value: typeValue.toString()},
            })
              .unwrap()
              .then(() => additionalInfoQuery.refetch().unwrap())
              .then((refreshed) => {
                const refreshedVariant = findByCode(refreshed, TEC_RMI_VARIANT_CODE);
                formApi.setValue('variant', getSelectedValue(refreshedVariant) ?? '');
              })
              .catch(handleApiError);
          };

          const handleVariantChange = (variantValue: string | number | Nullish) => {
            if (isNil(serviceVehicleId) || isNil(variantItem?.id) || isNil(variantValue)) {
              return;
            }

            patchAdditionalInformation({
              serviceVehicleId,
              additionalInformationId: variantItem.id,
              patchAdditionalInformationRequestBody: {value: variantValue.toString()},
            })
              .unwrap()
              .catch(handleApiError);
          };

          return (
            <VStack spacing={4}>
              <FormField
                type="choice"
                name="model"
                control={control}
                options={[{label: props.vehicleData.title, value: props.vehicleData.title}]}
                label={i18n.t('entity.vehicle.labels.model')}
                isDisabled
                menuInPortal
                data-testid={suffixTestId('model', props)}
              />
              <FormField
                type="number"
                name="mileage"
                control={control}
                label={`${i18n.t('entity.vehicle.labels.currentMileage')} (${i18n.t('general.metric.km')})`}
                isRequired
                data-testid={suffixTestId('mileage', props)}
              />
              <Show when={typeOptions.length > 0}>
                <FormField
                  type="choice"
                  name="type"
                  control={control}
                  options={typeOptions}
                  label={i18n.t('entity.vehicle.labels.vehicleType')}
                  isDisabled={isPatching || additionalInfoQuery.isFetching}
                  onChange={handleTypeChange}
                  menuInPortal
                  data-testid={suffixTestId('type', props)}
                />
              </Show>
              <Show when={variantOptions.length > 0}>
                <FormField
                  type="choice"
                  name="variant"
                  control={control}
                  options={variantOptions}
                  label={i18n.t('general.labels.variant')}
                  isDisabled={isPatching || additionalInfoQuery.isFetching}
                  onChange={handleVariantChange}
                  menuInPortal
                  data-testid={suffixTestId('variant', props)}
                />
              </Show>
              <ButtonGroup align="right">
                <FormButton
                  variant="secondary"
                  title={i18n.t('general.actions.cancel')}
                  onClick={props.onCancel}
                  control={control}
                  data-testid={suffixTestId('cancel', props)}
                />
                <FormButton
                  type="submit"
                  variant="primary"
                  control={control}
                  title={i18n.t('entity.vehicle.actions.generateServiceIntervals')}
                  data-testid={suffixTestId('submit', props)}
                />
              </ButtonGroup>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}

function findByCode(items: AdditionalInformationItem[], code: string) {
  return items.find((item) => item.code?.toLowerCase() === code.toLowerCase());
}

function toOptions(item: AdditionalInformationItem | Nullish) {
  return (item?.options ?? []).map((option) => ({
    label: option.description ?? option.value,
    value: option.value,
  }));
}

/**
 * Additional-information `value` is a record (`{value: '...'}`) for dropdown items, so read the
 * inner `value` and narrow it at runtime instead of asserting.
 */
function getSelectedValue(item: AdditionalInformationItem | Nullish) {
  const innerValue = item?.value?.value;
  return typeof innerValue === 'string' ? innerValue : null;
}

const schema = Yup.object().shape({
  mileage: Yup.number().required().min(0),
});
